import { createContext, useContext, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { trpc } from '../lib/trpc';

export type CurrentUser = {
  id: number;
  openId: string;
  name: string;
  email: string;
  loginMethod: 'email' | 'facebook';
  createdAt: Date;
};

interface AuthCtx {
  user: CurrentUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  refetch: () => void;
  logout: () => Promise<void>;
}
const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const meQuery = trpc.auth.me.useQuery(undefined, { retry: false });
  const logoutMut = trpc.auth.logout.useMutation();

  const user = (meQuery.data as CurrentUser | null) ?? null;
  const value: AuthCtx = {
    user,
    loading: meQuery.isLoading,
    isAuthenticated: !!user,
    refetch: () => void meQuery.refetch(),
    logout: async () => {
      await logoutMut.mutateAsync();
      await queryClient.resetQueries();
      window.location.href = '/login';
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within AuthProvider');
  return v;
}
